"use client";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  TextField,
  TablePagination,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import PageHeader1 from "../../components/common/PageHeader1";
import toast from "react-hot-toast";
import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

const OfferPopupList = () => {
  const token = localStorage.getItem("astroVani-admin-token");
  const [searchParams, setSearchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });

  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [searchType, setSearchType] = useState(getStringParam(searchParams, "searchType", "searchTitle"));
  const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", null));
  const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", null));

  const [applyFilter, setApplyFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const didInitUrlSyncRef = React.useRef(false);
  const applySawLoadingRef = React.useRef(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  /* ================= FETCH LIST ================= */
  const fetchList = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: paginationModel.page + 1, // backend 1-based
        limit: paginationModel.pageSize,
      });

      if (searchQuery) {
        params.append(searchType, searchQuery);
      }
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/offerPopup?${params.toString()}`,
        { headers: { Authorization: token } }
      );

      setList(res.data?.data || []);
      setPagination(res.data?.paginationDetail || {}); // ✅ FIX
    } catch {
      toast.error("Failed to fetch popup list");
    } finally {
      setLoading(false);
    }
  };


  /* 🔥 ONLY FILTER / PAGINATION CHANGE TRIGGERS API */
  useEffect(() => {
    fetchList();
  }, [paginationModel.page, paginationModel.pageSize, applyFilter]);

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (loading) {
      applySawLoadingRef.current = true;
      return;
    }
    if (!loading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [loading, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setIfTruthy(params, "search", searchQuery);
    setIfTruthy(params, "searchType", searchType);
    setIfTruthy(params, "startDate", startDate);
    setIfTruthy(params, "endDate", endDate);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, searchType, startDate, endDate, setSearchParams]);


  /* ================= FILTER & RESET ================= */
  const handleApplyFilter = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setApplyFilter((p) => !p);
  };

  const handleResetFilter = () => {
    setSearchQuery("");
    setSearchType("searchTitle");
    setStartDate(null);
    setEndDate(null);
    setPaginationModel({ page: 0, pageSize: 10 });
    setApplyFilter((p) => !p);
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASEURL}/admin/offerPopup/${selectedId}`,
        { headers: { Authorization: token } }
      );
      toast.success("Popup deleted successfully");
      fetchList();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  /* ================= EXPORT ================= */
  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      const params = new URLSearchParams({});
      if (searchQuery) {
        params.append(searchType, searchQuery);
      }
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/offerPopup?${params}`,
        { headers: { Authorization: token } }
      );

      const data = res.data?.data || [];

      const exportData = data.map((item, i) => ({
        "S.No": i + 1,
        "Popup Title": item.popupTitle,
        "Image For": item.imageFor,
        "Popup Type": item.popupType,
        Status: item.isActive ? "Active" : "Inactive",
        couponCode: item?.couponId?.couponCode || "-",
        "Created At": new Date(item.createdAt).toLocaleString(),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Offer Popups");
      XLSX.writeFile(wb, "offer_popup_list.xlsx");

      toast.success("Exported successfully");
    } catch {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  /* ================= TABLE ================= */
  const rows = useMemo(
    () =>
      list.map((item, index) => ({
        id: item._id,
        sono: paginationModel.page * paginationModel.pageSize + index + 1,
        popupTitle: item.popupTitle,
        imageFor: item.imageFor,
        popupType: item.popupType,
        status: item.isActive || false,
        createdAt: new Date(item.createdAt),
        couponId: item?.couponId?._id || "-",
        couponCode: item?.couponId?.couponCode || "-",
      })),
    [list, paginationModel]
  );

  const handleStatusChange = async (id, status) => {
    try {
      const isStatis = !status;
      setLoading(true);
      await axios.patch(
        `${process.env.REACT_APP_BASEURL}/admin/togglePopup/${id}`,
        { isActive: isStatis },
        { headers: { Authorization: token } }
      );
      toast.success("Status updated successfully");
      fetchList();
    } catch {
      toast.error("Status update failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "sono", headerName: "S.No", width: 80 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) => (
        <>
          {row.couponId == "-" && row.couponCode == "-" && <IconButton component={Link} to={`/offer-add?id=${row.id}`}>
            <i className="icofont-edit" />
          </IconButton>}
          <IconButton onClick={() => { setSelectedId(row.id); setOpen(true); }}>
            <i className="icofont-ui-delete text-danger" />
          </IconButton>
        </>
      ),
    },
    { field: "popupTitle", headerName: "Popup Title", width: 250 },
    { field: "imageFor", headerName: "Image For", width: 180 },
    { field: "popupType", headerName: "Popup Type", width: 150 },
    { field: "couponCode", headerName: "Coupon Code", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => (
        <Switch
          checked={row.status}
          onChange={() => handleStatusChange(row.id, row.status)}
        />
      ),
    },
    { field: "createdAt", headerName: "Created At", width: 200, type: "dateTime" },
  ];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1
          pagetitle="Offer Popup List"
          righttitle="Add Popup"
          link="/offer-add"
          routebutton
        />

        {/* FILTER BAR */}
        <div className="card mb-3">
          <div className="card-body">
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 150 }, flex: { xs: "1 1 100%", md: "auto" } }}>
                <InputLabel id="search-type-label">Search By</InputLabel>
                <Select
                  labelId="search-type-label"
                  value={searchType}
                  label="Search By"
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <MenuItem value="searchTitle">Search Title</MenuItem>
                  <MenuItem value="couponCode">Coupon Code</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Search"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: { xs: "1 1 100%", md: "auto" } }}
              />
              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={startDate || ""}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ flex: { xs: "1 1 100%", md: "auto" } }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={endDate || ""}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ flex: { xs: "1 1 100%", md: "auto" } }}
              />

              <Box sx={{ display: "flex", gap: 2, width: { xs: "100%", md: "auto" } }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilter}
                  disabled={loading || isApplyingFilters}
                >
                  {isApplyingFilters ? "Applying..." : "Filter"}
                </Button>
                <Button variant="outlined" fullWidth onClick={handleResetFilter}>
                  Reset
                </Button>
              </Box>

              <Button
                variant="contained"
                sx={{ ml: { md: "auto" }, width: { xs: "100%", md: "auto" } }}
                onClick={exportToExcel}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "EXPORT"}
              </Button>
            </Box>
          </div>
        </div>

        {/* TABLE */}
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          hideFooterPagination
          paginationMode="server"   // ✅ IMPORTANT
        />

        <TablePagination
          component="div"
          count={pagination?.totalDocs || 0}  // ✅ now works
          page={paginationModel.page}
          onPageChange={(_, newPage) =>
            setPaginationModel((prev) => ({ ...prev, page: newPage }))
          }
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(e) =>
            setPaginationModel({
              page: 0,
              pageSize: parseInt(e.target.value, 10),
            })
          }
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      {/* DELETE MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Delete Popup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this popup?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OfferPopupList;
